import LATIN_WORDS from './latinWords';

/**
 * Generate a random anonymized username from two Latin words + a number.
 * Examples: AquilaFortis42, LuxAeterna217, VentusAureus8
 */
export const generateAnonymizedUsername = () => {
  const pickRandom = () => LATIN_WORDS[Math.floor(Math.random() * LATIN_WORDS.length)];
  let word1 = pickRandom();
  let word2 = pickRandom();
  // Ensure the two words are different
  while (word2 === word1) {
    word2 = pickRandom();
  }
  const num = Math.floor(Math.random() * 900) + 1; // 1–900
  return `${word1}${word2}${num}`;
};

/**
 * Get the bounty count range display text
 * @param {number} count - Number of completed bounties
 * @returns {string} Display text like "1-10", "10-100", "100+"
 */
export const getBountyCountRange = (count) => {
  if (count === 0) return '0';
  if (count >= 1 && count < 10) return '1-10';
  if (count >= 10 && count < 100) return '10-100';
  if (count >= 100) return '100+';
  return '0';
};

/**
 * Create a masked reputation object for fully anonymous users.
 * Rounds rating to nearest whole star.
 * @param {object} reputation - The raw reputation object
 * @returns {object} Masked reputation with rounded values
 */
export const maskReputation = (reputation) => {
  if (!reputation) return null;
  const { rating = 0, completedBounties = 0 } = reputation;
  return {
    rating: Math.round(rating),
    totalRatings: 0,
    completedBounties,
  };
};

/**
 * Check if an anonymized username is unique in Firestore
 * @param {string} username - The username to check
 * @param {import('firebase/firestore').Firestore} db - Firestore instance
 * @returns {Promise<boolean>} True if unique, false if exists
 */
export const isUsernameUnique = async (username, db) => {
  const { query, collection, where, getDocs } = await import('firebase/firestore');

  const q = query(
    collection(db, 'users'),
    where('anonymizedUsername', '==', username)
  );

  const snapshot = await getDocs(q);
  return snapshot.empty;
};

/**
 * Generate a unique anonymized username
 * @param {import('firebase/firestore').Firestore} db - Firestore instance
 * @returns {Promise<string>} A unique anonymized username
 */
export const generateUniqueAnonymizedUsername = async (db) => {
  let username;
  let attempts = 0;
  const maxAttempts = 10;

  do {
    username = generateAnonymizedUsername();
    const unique = await isUsernameUnique(username, db);

    if (unique) {
      return username;
    }

    attempts++;
  } while (attempts < maxAttempts);

  // If we've tried 10 times and still haven't found a unique username,
  // add a timestamp to ensure uniqueness
  return `User_${Date.now().toString(36).toUpperCase().slice(-4)}`;
};
